import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Loader2, Search, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { businessesService, type AvailablePermission } from '@services/businesses.service';

function apiMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown };
    if (typeof e.message === 'string' && e.message.trim()) return e.message;
  }
  return fallback;
}

const MODULE_ORDER = [
  'businesses', 'branches', 'staff', 'roles', 'departments', 'products', 'inventory',
  'orders', 'payments', 'wallets', 'delivery', 'riders', 'kyc', 'promotions', 'ads',
  'reports', 'audit', 'commissions', 'brands', 'categories', 'users',
];

function groupByModule(perms: AvailablePermission[]) {
  const map: Record<string, AvailablePermission[]> = {};
  for (const p of perms) (map[p.code.split('.')[0]] ??= []).push(p);
  const ordered = MODULE_ORDER.filter((m) => map[m]).map((m) => ({ module: m, perms: map[m] }));
  const rest = Object.keys(map).filter((m) => !MODULE_ORDER.includes(m)).map((m) => ({ module: m, perms: map[m] }));
  return [...ordered, ...rest];
}

/**
 * Ops-side management of a business staff member's specialized access:
 * business roles (assign/remove) and per-permission overrides (grant / deny /
 * revoke), scoped to the business. Mirrors the merchant portal's own controls.
 */
export function StaffAccessModal({ businessId, staff, onClose }: {
  businessId: string; staff: any; onClose: () => void;
}) {
  const qc = useQueryClient();
  const userId: string | undefined = staff.user?.userId ?? staff.userId;
  const name = [staff.user?.firstName, staff.user?.lastName].filter(Boolean).join(' ') || staff.user?.phone || 'Staff member';
  const assignedRoleIds = new Set<string>((staff.roles ?? []).map((r: any) => r.roleId ?? r.id));

  const [search, setSearch] = useState('');
  const [tab, setTab] = useState<'permissions' | 'roles'>('permissions');

  const rolesQ = useQuery({ queryKey: ['biz-roles', businessId], queryFn: () => businessesService.listBusinessRoles(businessId) });
  const availQ = useQuery({ queryKey: ['biz-perms', businessId], queryFn: () => businessesService.listAvailablePermissions(businessId) });
  const grantsQ = useQuery({ queryKey: ['user-grants', businessId, userId], queryFn: () => businessesService.listUserGrants(businessId, userId!), enabled: !!userId });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['user-grants', businessId, userId] });
    qc.invalidateQueries({ queryKey: ['business-staff', businessId] });
  };

  const grant = useMutation({
    mutationFn: ({ permissionId, isDeny }: { permissionId: string; isDeny: boolean }) =>
      businessesService.grantPermission(businessId, userId!, permissionId, isDeny),
    onSuccess: invalidate,
    onError: (e) => toast.error(apiMessage(e, 'Could not update permission.')),
  });
  const revoke = useMutation({
    mutationFn: (permissionId: string) => businessesService.revokePermission(businessId, userId!, permissionId),
    onSuccess: invalidate,
    onError: (e) => toast.error(apiMessage(e, 'Could not revoke permission.')),
  });
  const assignRole = useMutation({
    mutationFn: (roleId: string) => businessesService.assignRole(businessId, userId!, roleId),
    onSuccess: () => { toast.success('Role assigned.'); invalidate(); },
    onError: (e) => toast.error(apiMessage(e, 'Could not assign role.')),
  });
  const removeRole = useMutation({
    mutationFn: (roleId: string) => businessesService.removeRole(businessId, userId!, roleId),
    onSuccess: () => { toast.success('Role removed.'); invalidate(); },
    onError: (e) => toast.error(apiMessage(e, 'Could not remove role.')),
  });

  const grantByPerm = new Map((grantsQ.data ?? []).map((g) => [g.permissionId, g]));
  const filtered = search.trim()
    ? (availQ.data ?? []).filter((p) => p.code.toLowerCase().includes(search.toLowerCase()) || p.name.toLowerCase().includes(search.toLowerCase()))
    : (availQ.data ?? []);
  const groups = groupByModule(filtered);

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:p-8" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="mt-4 w-full max-w-lg rounded-2xl border border-[var(--border-subtle)] bg-[var(--bg-surface)] p-6 shadow-xl">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <h3 className="flex items-center gap-2 text-base font-semibold text-[var(--text-primary)]"><ShieldCheck size={17} className="text-[var(--text-accent)]" /> Access & permissions</h3>
            <p className="text-[13px] text-[var(--text-muted)]">{name}</p>
          </div>
          <button onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--text-primary)]"><X size={18} /></button>
        </div>

        {!userId ? (
          <p className="py-6 text-[13px] text-[var(--text-muted)]">This staff member has no linked user account yet (invite pending), so access can't be set.</p>
        ) : (
          <>
            <div className="mb-4 flex gap-1 rounded-full bg-[var(--bg-base)] p-1 w-fit">
              {(['permissions', 'roles'] as const).map((t) => (
                <button key={t} onClick={() => setTab(t)}
                  className={`rounded-full px-4 py-1.5 text-[13px] font-semibold capitalize transition-colors ${tab === t ? 'bg-[var(--bg-surface)] text-[var(--text-primary)] shadow-sm' : 'text-[var(--text-muted)]'}`}>
                  {t}
                </button>
              ))}
            </div>

            {tab === 'permissions' ? (
              <>
                {(grantsQ.data ?? []).length > 0 && (
                  <div className="mb-3 rounded-xl border border-[var(--border-subtle)] bg-[var(--bg-base)] p-3">
                    <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[var(--text-muted)]">Active overrides</p>
                    <div className="flex flex-wrap gap-1.5">
                      {(grantsQ.data ?? []).map((g) => (
                        <span key={g.userPermissionId} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[12px] font-medium ${g.grantType === 'deny' ? 'border-[var(--state-danger-border)] bg-[var(--state-danger-bg)] text-[var(--state-danger)]' : 'border-[var(--state-success-border)] bg-[var(--state-success-bg)] text-[var(--state-success)]'}`}>
                          {g.grantType === 'deny' ? '✕' : '✓'} {g.code}
                          <button onClick={() => revoke.mutate(g.permissionId)} className="opacity-60 hover:opacity-100" title="Remove override">×</button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                <div className="relative mb-3">
                  <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                  <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search permissions…"
                    className="w-full rounded-full border border-[var(--border-default)] bg-[var(--bg-base)] pl-9 pr-4 py-2 text-[13px] text-[var(--text-primary)] outline-none" />
                </div>
                {availQ.isLoading || grantsQ.isLoading ? (
                  <div className="flex items-center gap-2 py-6 text-[13px] text-[var(--text-muted)]"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
                ) : (
                  <div className="max-h-96 space-y-4 overflow-y-auto pr-1">
                    {groups.map(({ module, perms }) => (
                      <div key={module}>
                        <p className="mb-1.5 px-1 text-[10px] font-semibold uppercase tracking-wide text-[var(--text-muted)]">{module}</p>
                        <div className="space-y-1">
                          {perms.map((p) => {
                            const g = grantByPerm.get(p.permissionId);
                            return (
                              <div key={p.permissionId} className="flex items-center justify-between gap-2 rounded-xl border border-[var(--border-subtle)] px-3 py-2">
                                <div className="min-w-0">
                                  <p className="truncate text-[13px] font-medium text-[var(--text-primary)]">{p.code}</p>
                                  <p className="truncate text-[11px] text-[var(--text-muted)]">{p.name}</p>
                                </div>
                                <div className="flex shrink-0 gap-1">
                                  <button disabled={grant.isPending} onClick={() => grant.mutate({ permissionId: p.permissionId, isDeny: false })}
                                    className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold ${g?.grantType === 'grant' ? 'border-[var(--state-success-border)] bg-[var(--state-success-bg)] text-[var(--state-success)]' : 'border-[var(--border-default)] text-[var(--text-muted)] hover:text-[var(--text-primary)]'}`}>Allow</button>
                                  <button disabled={grant.isPending} onClick={() => grant.mutate({ permissionId: p.permissionId, isDeny: true })}
                                    className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold ${g?.grantType === 'deny' ? 'border-[var(--state-danger-border)] bg-[var(--state-danger-bg)] text-[var(--state-danger)]' : 'border-[var(--border-default)] text-[var(--text-muted)] hover:text-[var(--text-primary)]'}`}>Deny</button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="max-h-96 space-y-2 overflow-y-auto pr-1">
                {rolesQ.isLoading ? (
                  <div className="flex items-center gap-2 py-6 text-[13px] text-[var(--text-muted)]"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
                ) : (rolesQ.data ?? []).length === 0 ? (
                  <p className="py-6 text-center text-[13px] text-[var(--text-muted)]">This business has no custom roles. Create roles first, or use permission overrides.</p>
                ) : (
                  (rolesQ.data ?? []).map((role) => {
                    const assigned = assignedRoleIds.has(role.roleId);
                    return (
                      <div key={role.roleId} className="flex items-center justify-between gap-3 rounded-xl border border-[var(--border-subtle)] p-3">
                        <div className="min-w-0">
                          <p className="truncate text-[14px] font-medium text-[var(--text-primary)]">{role.name}</p>
                          {role.description && <p className="truncate text-[12px] text-[var(--text-muted)]">{role.description}</p>}
                        </div>
                        <button disabled={assignRole.isPending || removeRole.isPending}
                          onClick={() => assigned ? removeRole.mutate(role.roleId) : assignRole.mutate(role.roleId)}
                          className={`shrink-0 rounded-full border px-3 py-1.5 text-[12px] font-semibold ${assigned ? 'border-[var(--state-danger-border)] text-[var(--state-danger)] hover:bg-[var(--state-danger-bg)]' : 'border-[var(--border-default)] text-[var(--text-primary)] hover:bg-[var(--bg-base)]'}`}>
                          {assigned ? 'Remove' : 'Assign'}
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
